/**
 * Assessment forms on screen (migrations 0065, 0068).
 *
 * What makes a questionnaire valid and how its answers add up. The database
 * decides both again (app.form_problem, app.score_form_answers), in the same
 * words; this lets the editor and the in-session form say so before saving.
 *
 * Four kinds of question. A `scale` runs from a number to a bigger one with a
 * label per value. A `choice` lists its options in the order they are written,
 * each with its own score, and the answer is the position chosen — CDI prints
 * some items 2,1,0 and others 0,1,2, and a form has to keep that order. A
 * `yes_no` adds one for yes. A `text` answer is never scored.
 *
 * A form may also name subscales: [{label, items:[question numbers, from 1],
 * bands}], each scored over its own questions.
 */

export const QUESTION_KINDS = ['scale', 'yes_no', 'text', 'choice'];

export const blankQuestion = (kind = 'scale') => {
  if (kind === 'scale') return { kind, prompt: '', min: 0, max: 3, labels: ['', '', '', ''] };
  if (kind === 'choice') return { kind, prompt: '', options: [{ label: '', score: 0 }, { label: '', score: 1 }] };
  return { kind, prompt: '' };
};

/** A scale with a new range, keeping the labels of the values still in it. */
export const withRange = (question, min, max) => {
  const labels = [];
  for (let value = min; value <= max; value += 1) {
    const before = value - Number(question.min ?? 0);
    const kept = question.labels ?? [];
    labels.push(before >= 0 && before < kept.length ? kept[before] : '');
  }
  return { ...question, min, max, labels };
};

const isWhole = (value) => /^[0-9]+$/.test(String(value ?? ''));

/** The most one question can add to the total. */
export const questionTop = (question) => {
  if (question?.kind === 'scale') return Number(question.max) || 0;
  if (question?.kind === 'yes_no') return 1;
  if (question?.kind === 'choice') {
    return (question.options ?? []).reduce((best, option) => Math.max(best, Number(option?.score) || 0), 0);
  }
  return 0;
};

/** The highest total a form can reach. */
export const maxTotal = (questions) => questions.reduce((sum, question) => sum + questionTop(question), 0);

/** The highest a subscale can reach, over the questions it names. */
export const scaleTop = (questions, items) => (items ?? []).reduce(
  (sum, item) => sum + questionTop(questions[Number(item) - 1]), 0,
);

/** What is wrong with a form, in the database's words, or null. */
export const formProblem = (questions, bands, scales = []) => {
  if (!Array.isArray(questions) || questions.length === 0) return 'Un formulario necesita al menos una pregunta.';
  if (questions.length > 60) return 'Un formulario puede tener hasta 60 preguntas.';

  for (let i = 0; i < questions.length; i += 1) {
    const question = questions[i];
    const n = i + 1;
    if (!QUESTION_KINDS.includes(question?.kind)) return `La pregunta ${n} no tiene un tipo válido.`;
    // A choice question's options are its text; the other kinds need a prompt.
    if (question.kind !== 'choice' && !String(question.prompt ?? '').trim()) {
      return `A la pregunta ${n} le falta el texto.`;
    }
    if (String(question.prompt ?? '').length > 1000) return `El texto de la pregunta ${n} es demasiado largo.`;

    if (question.kind === 'scale') {
      if (!isWhole(question.min) || !isWhole(question.max)) {
        return `La escala de la pregunta ${n} necesita un mínimo y un máximo enteros.`;
      }
      const min = Number(question.min);
      const max = Number(question.max);
      if (max <= min || max > 10) {
        return `La escala de la pregunta ${n} tiene que ir de un número a otro mayor, hasta 10.`;
      }
      if (!Array.isArray(question.labels) || question.labels.length !== max - min + 1) {
        return `La escala de la pregunta ${n} necesita una etiqueta por cada valor.`;
      }
      if (question.labels.some((label) => typeof label !== 'string' || !label.trim())) {
        return `A la escala de la pregunta ${n} le falta alguna etiqueta.`;
      }
    }

    if (question.kind === 'choice') {
      const options = question.options;
      if (!Array.isArray(options) || options.length < 2 || options.length > 10) {
        return `La pregunta ${n} necesita entre 2 y 10 opciones.`;
      }
      for (let j = 0; j < options.length; j += 1) {
        const option = options[j];
        const m = j + 1;
        if (!isWhole(option?.score) || Number(option.score) > 10) {
          return `La opción ${m} de la pregunta ${n} necesita un puntaje entero de 0 a 10.`;
        }
        if (!String(option.label ?? '').trim()) return `A la opción ${m} de la pregunta ${n} le falta el texto.`;
        if (String(option.label ?? '').length > 1000) {
          return `El texto de la opción ${m} de la pregunta ${n} es demasiado largo.`;
        }
      }
    }
  }

  if (!Array.isArray(bands)) return 'Los rangos del puntaje no son válidos.';
  const top = maxTotal(questions);
  for (let i = 0; i < bands.length; i += 1) {
    const band = bands[i];
    const n = i + 1;
    if (!isWhole(band?.from) || !isWhole(band?.to)) return `El rango ${n} necesita un desde y un hasta enteros.`;
    if (Number(band.from) > Number(band.to) || Number(band.to) > top) {
      return `El rango ${n} tiene que ir de menor a mayor, hasta el puntaje máximo (${top}).`;
    }
    if (!String(band.label ?? '').trim()) return `Al rango ${n} le falta el nombre.`;
  }
  for (let i = 0; i < bands.length; i += 1) {
    for (let j = i + 1; j < bands.length; j += 1) {
      if (Number(bands[i].from) <= Number(bands[j].to) && Number(bands[j].from) <= Number(bands[i].to)) {
        return 'Dos rangos del puntaje se superponen.';
      }
    }
  }

  if (!Array.isArray(scales)) return 'Las subescalas no son válidas.';
  if (scales.length > 20) return 'Un formulario puede tener hasta 20 subescalas.';
  for (let i = 0; i < scales.length; i += 1) {
    const scale = scales[i];
    const n = i + 1;
    if (!String(scale?.label ?? '').trim()) return `A la subescala ${n} le falta el nombre.`;
    if (!Array.isArray(scale.items) || scale.items.length === 0) {
      return `La subescala ${n} necesita al menos una pregunta.`;
    }
    if (new Set(scale.items.map(String)).size !== scale.items.length) {
      return `La subescala ${n} repite una pregunta.`;
    }
    for (const item of scale.items) {
      const number = Number(item);
      if (!Number.isInteger(number) || number < 1 || number > questions.length) {
        return `La subescala ${n} nombra una pregunta que no existe.`;
      }
      if (questionTop(questions[number - 1]) === 0) {
        return `La subescala ${n} incluye la pregunta ${number}, que no suma puntaje.`;
      }
    }
    const scaleMax = scaleTop(questions, scale.items);
    for (const band of scale.bands ?? []) {
      if (!isWhole(band?.from) || !isWhole(band?.to)
        || Number(band.from) > Number(band.to) || Number(band.to) > scaleMax) {
        return `Un rango de la subescala ${n} tiene que ir de menor a mayor, hasta su puntaje máximo (${scaleMax}).`;
      }
      if (!String(band.label ?? '').trim()) return `A un rango de la subescala ${n} le falta el nombre.`;
    }
  }

  return null;
};

const bandFor = (bands, total) => (bands ?? [])
  .find((row) => total >= Number(row.from) && total <= Number(row.to))?.label ?? null;

/**
 * The total, its named range, each subscale's score, and the first scored
 * question still unanswered (counting from 1), or null when none is. Text
 * answers never count.
 */
export const scoreAnswers = (questions, bands, answers, scales = []) => {
  const values = [];
  let missing = null;
  questions.forEach((question, i) => {
    const answer = answers?.[i];
    if (question.kind === 'scale') {
      if (typeof answer !== 'number') { if (missing === null) missing = i + 1; values.push(0); return; }
      values.push(answer);
    } else if (question.kind === 'choice') {
      const options = question.options ?? [];
      if (typeof answer !== 'number' || !options[answer]) {
        if (missing === null) missing = i + 1;
        values.push(0);
        return;
      }
      values.push(Number(options[answer].score) || 0);
    } else if (question.kind === 'yes_no') {
      if (typeof answer !== 'boolean') { if (missing === null) missing = i + 1; values.push(0); return; }
      values.push(answer ? 1 : 0);
    } else {
      values.push(0);
    }
  });

  const total = values.reduce((sum, value) => sum + value, 0);
  const scaleScores = (scales ?? []).map((scale) => {
    const scaleTotal = (scale.items ?? []).reduce((sum, item) => sum + (values[Number(item) - 1] ?? 0), 0);
    return { label: scale.label, total: scaleTotal, band: bandFor(scale.bands, scaleTotal) };
  });

  return { total, band: bandFor(bands, total), missing, scales: scaleScores };
};
